import { ReactNode } from 'react';
import { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/core/form';

type DefaultFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = ControllerProps<TFieldValues, TName> & {
  label?: ReactNode;
  description?: ReactNode;
  hideError?: boolean;
};

/**
 * For use with FormField subcomponents that wrap DefaultFormField for a specific input type.
 *
 * E.g.
 *
 * ```ts
 * function MyFormField(props: FormFieldProps<MyInputProps, TFieldValues, TName>) {
 *   return <DefaultFormField ... />
 * }
 * ```
 */
export type FormFieldProps<
  Props,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Props & Omit<DefaultFormFieldProps<TFieldValues, TName>, 'render' | 'rules' | 'defaultValue'>;

/**
 * Provides a default FormField structure with optional label/description and an error message (which can be disabled).
 * The render method is used to render the form control itself.
 */
export function DefaultFormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ render, label, description, hideError, ...props }: DefaultFormFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      {...props}
      render={({ field, fieldState, formState }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>{render({ field, fieldState, formState })}</FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          {!hideError && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
